import React, { useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import logo from '../../img/logo.svg';
import { downloadReportPdf, reportNumberFormat, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import {
  OtherReportFilterCard,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportShell,
  OtherReportToolbar,
  OtherReportActionButtons,
} from './other-reports/OtherReportShared';

const formatCell = (value) => reportNumberFormat(value, { empty: '' });

const TradeProfitLossReport = (props) => {
  const [inputValues, setInputValues] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleInput = (e) => {
    setInputValues({
      ...inputValues,
      [e.target.name]: e.target.value,
    });
  };

  const validateDates = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateDates()) {
      return;
    }
    loadReport();
  };

  const downloadPdf = async () => {
    if (!validateDates()) {
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/trade_loss_gain/pdf`,
        {
          from_date: inputValues.start_date,
          to_date: inputValues.end_date,
          start_date: inputValues.start_date,
          end_date: inputValues.end_date,
        },
        `trade-profit-loss-${inputValues.start_date}-to-${inputValues.end_date}.pdf`,
      );
    } catch (error) {
      swal('PDF export', error.message || 'Unable to download PDF.', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  async function loadReport() {
    setLoading(true);
    setReport(null);

    try {
      const response = await fetch(`${API_URL}/trade_loss_gain`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
          Accept: 'application/json',
        },
        body: JSON.stringify({
          from_date: inputValues.start_date,
          to_date: inputValues.end_date,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status === false) {
        throw new Error(data.message || 'Unable to load trade profit and loss report.');
      }

      setReport(data);
    } catch (error) {
      swal('Trade Profit & Loss', error.message || 'Something went wrong.', 'error');
    } finally {
      setLoading(false);
    }
  }

  const renderLedgerSection = (section) => {
    if (!section?.rows?.length) {
      return null;
    }

    return (
      <div className="table-widget mb-4" key={section.title}>
        <h3 className="table-title text-start">{section.title}</h3>
        <table className="table border">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product name</th>
              <th>Description</th>
              <th>Avg purchase price</th>
              <th>Avg sales price</th>
              <th>Quantity</th>
              <th>Margin per unit</th>
              <th>Debit (Rs)</th>
              <th>Credit (Rs)</th>
              <th>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, index) => (
              <tr key={`${section.title}-${index}`}>
                <td>{formatReportDate(row.date)}</td>
                <td>{row.product_name || ''}</td>
                <td>{row.description || ''}</td>
                <td className="report-amount">{formatCell(row.purchase_price)}</td>
                <td className="report-amount">{formatCell(row.sales_price)}</td>
                <td>{row.quantity ?? ''}</td>
                <td className="report-amount">{formatCell(row.margin_per_unit)}</td>
                <td className="report-amount">{formatCell(row.dr_amount)}</td>
                <td className="report-amount">{formatCell(row.cr_amount)}</td>
                <td className="report-amount">{formatCell(row.outstanding)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDirectSalesSection = (title, section, columns) => {
    if (!section?.rows?.length) {
      return null;
    }

    return (
      <div className="table-widget mb-4">
        <h3 className="table-title text-start">{title}</h3>
        <table className="table border">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, index) => (
              <tr key={`${title}-${index}`}>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.format ? column.format(row[column.key]) : (row[column.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td colSpan={columns.length - 2} className="text-end fw-bold">Total</td>
              <td className="report-amount fw-bold">{formatCell(section.totals?.profit_sum)}</td>
              <td className="report-amount fw-bold">{formatCell(section.totals?.outstanding_sum)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const fuelLedger = report?.fuel_ledger;

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={26} operation={4} op={4} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />

          <OtherReportShell>
            <OtherReportPageHeader title="Trade Profit & Loss" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={handleSubmit}
                    onDownloadPdf={downloadPdf}
                    submitLabel="Generate"
                    pdfLabel="Export PDF"
                  />
                )}
              />
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating trade P&amp;L" />}
              {report && !loading && (
              <div className="form-wrap balace-sheetwrap">
                <div className="row mb-3">
                  <div className="col-md">
                    <h2 className="table-title mb-1">{report.pump_name || 'COMPANY NAME'}</h2>
                    <h4 className="table-title mb-1">Trade Profit and Loss Statement</h4>
                    <h5>
                      {formatReportDateRange(report.start_date, report.end_date)}
                    </h5>
                    <img src={logo} className="img-fluid float-end" width="100px" alt="logo" />
                  </div>
                </div>

                {renderLedgerSection(fuelLedger?.sales)}
                {renderLedgerSection(fuelLedger?.cost_of_goods_sold)}
                {renderLedgerSection(fuelLedger?.income)}
                {renderLedgerSection(fuelLedger?.expense)}

                {fuelLedger?.totals && (
                  <div className="table-widget mb-4">
                    <table className="table border">
                      <tbody>
                        <tr>
                          <td colSpan="7" className="text-end fw-bold">Fuel ledger total</td>
                          <td className="report-amount fw-bold">{formatCell(fuelLedger.totals.dr_total)}</td>
                          <td className="report-amount fw-bold">{formatCell(fuelLedger.totals.cr_total)}</td>
                          <td className="report-amount fw-bold">{formatCell(fuelLedger.totals.outstanding)}</td>
                        </tr>
                        <tr>
                          <td colSpan="9" className="text-end fw-bold">
                            {fuelLedger.totals.sign}{formatCell(fuelLedger.totals.dr_total)} ({fuelLedger.totals.profit_loss})
                          </td>
                          <td className="fw-bold">
                            {fuelLedger.totals.sign}{formatCell(fuelLedger.totals.outstanding)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {renderDirectSalesSection('Non fuel', report.non_fuel, [
                  { key: 'date', label: 'Date', format: formatReportDate },
                  { key: 'product_name', label: 'Product name' },
                  { key: 'purchase_price_exc_gst', label: 'Purchase price exc GST', format: formatCell },
                  { key: 'mrp_per_unit', label: 'MRP', format: formatCell },
                  { key: 'quantity', label: 'Quantity' },
                  { key: 'gst', label: 'GST', format: formatCell },
                  { key: 'return_gst', label: 'Return GST', format: formatCell },
                  { key: 'profit', label: 'Profit', format: formatCell },
                  { key: 'outstanding', label: 'Outstanding', format: formatCell },
                ])}

                {renderDirectSalesSection('Barrel sales', report.barrel, [
                  { key: 'date', label: 'Date', format: formatReportDate },
                  { key: 'product_name', label: 'Product name' },
                  { key: 'full_barrel_mrp', label: 'Full barrel MRP', format: formatCell },
                  { key: 'per_litre_mrp', label: 'Per lt MRP', format: formatCell },
                  { key: 'full_barrel_qty', label: 'Full barrel' },
                  { key: 'loose_barrel_qty', label: 'Loose barrel (lt)' },
                  { key: 'machine_qty', label: 'Machine sales (lt)' },
                  { key: 'total_mrp', label: 'Total MRP', format: formatCell },
                  { key: 'purchase_price_exc_gst', label: 'Purchase price exc GST', format: formatCell },
                  { key: 'gst', label: 'GST', format: formatCell },
                  { key: 'return_gst', label: 'Return GST', format: formatCell },
                  { key: 'profit', label: 'Profit', format: formatCell },
                  { key: 'outstanding', label: 'Outstanding', format: formatCell },
                ])}

                {renderDirectSalesSection('Gas cylinder sales', report.cylinder, [
                  { key: 'date', label: 'Date', format: formatReportDate },
                  { key: 'product_name', label: 'Product name' },
                  { key: 'sales_type', label: 'Sales type' },
                  { key: 'quantity', label: 'Quantity' },
                  { key: 'mrp', label: 'MRP', format: formatCell },
                  { key: 'purchase_price_exc_gst', label: 'Purchase price exc GST', format: formatCell },
                  { key: 'gst', label: 'GST', format: formatCell },
                  { key: 'return_gst', label: 'Return GST', format: formatCell },
                  { key: 'profit', label: 'Profit', format: formatCell },
                  { key: 'outstanding', label: 'Outstanding', format: formatCell },
                ])}

                {report.final && (
                  <div className="alert alert-info mt-3">
                    Final position: <strong>{report.final.profit_loss}</strong> — outstanding{' '}
                    <strong>{report.final.sign}{formatCell(report.final.outstanding)}</strong>
                  </div>
                )}
              </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default TradeProfitLossReport;
